import type { ReactNode } from 'react';
import type { Edge } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object-graph.tsx';
import { getReferenceCheckedLocalStorage } from './storage/index.tsx';
import type { ReferenceType } from './types.tsx';

type GetReferenceTypes = (arg1: {
	nodes: ReactNode[];
	edges: Edge[];
	referenceDepth: number;
	initialObjectTypeId: string;
}) => ReferenceType[];

const getReferenceCheckedState = (
	objectTypeId: string,
	referenceLabel: string,
	showByDefault?: boolean,
): boolean => {
	const defaultCheckedState = showByDefault ?? true;
	return getReferenceCheckedLocalStorage({ objectTypeId, referenceLabel }) ?? defaultCheckedState;
};

export const getReferenceTypes: GetReferenceTypes = ({
	nodes,
	edges,
	referenceDepth,
	initialObjectTypeId,
}) => {
	// we accumulate a set of reference types that store all edges between nodes that are associated with that reference
	// this is used render nodes / edges in the graph and to filter out nodes in the graph based on whether the reference type is checked or not
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const referenceTypes = edges.reduce<Array<any>>(
		(referenceTypeList: ReferenceType[], edge: Edge) => {
			const reference = {
				label: edge.label,
				color: edge.color,
				from: [edge.from],
				to: [edge.to],
				checked: true,
				count: 1,
			};

			// for each edge we validate the node/s it connects are valid before associating it with a reference
			// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'. | TS2339 - Property 'id' does not exist on type 'boolean | ReactChild | ReactFragment | ReactPortal'.
			const hasSourceNode = nodes.some((n) => n.id === edge.from.slice(1));
			const hasDestinationNode = nodes.some(
				// @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'. | TS2339 - Property 'id' does not exist on type 'boolean | ReactChild | ReactFragment | ReactPortal'. | TS2533 - Object is possibly 'null' or 'undefined'. | TS2339 - Property 'depth' does not exist on type 'boolean | ReactChild | ReactFragment | ReactPortal'.
				(n) => n.id === edge.to.slice(1) && n.depth <= referenceDepth,
			);

			const isValidEdge = hasDestinationNode && hasSourceNode;
			const existingReference = referenceTypeList.find((r) => r.label === reference.label);

			if (existingReference && isValidEdge) {
				existingReference.from.push(edge.from);
				existingReference.to.push(edge.to);
				existingReference.count += 1;
				existingReference.checked = getReferenceCheckedState(
					initialObjectTypeId,
					edge.label,
					existingReference.count < 10, // we want display reference types with < 10 distinct edges in the graph by default
				);
			} else if (isValidEdge) {
				reference.checked = getReferenceCheckedState(initialObjectTypeId, edge.label);
				referenceTypeList.push(reference);
			}

			return referenceTypeList;
		},
		[],
	);

	return referenceTypes;
};
